import { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-grid-system';
import styled from 'styled-components';
import { BackButton, LoadingSpinner } from '../atoms';
import { useLocation, useNavigate } from 'react-router';
import {
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  useUserLanguage,
} from '@/customHooks';
import { CreateSupplierForm } from '../organism';
import { SupplierDetail } from '@/custom-types';
import { CreateSupplierMutation, UpdateSupplierMutation, useGetSupplierDetailLazyQuery } from '@/graphql/generated';
import { FetchResult } from '@apollo/client';

const textsData = {
  en: {
    title: 'Supplier',
    create: 'Add',
    edit: 'Edit',
    detail: 'Deatil',
    confirmation: {
      create: {
        title: 'Supplier has been created',
        description: (firstName: string, lastName: string) => `User ${firstName} ${lastName} has been created succesfully`,
      },
      update: {
        title: 'Supplier has been updated',
        description: (firstName: string, lastName: string) => `User ${firstName} ${lastName} has been updated succesfully`,
      }
    }
  },
  es: {
    title: 'Proveedor',
    create: 'Agregar',
    edit: 'Editar',
    detail: ', detalles',
    confirmation: {
      create: {
        title: 'Proveedor creado con éxito',
        description: (firstName: string, lastName: string) => `${firstName} ${lastName} fue creado con éxito`,
      },
      update: {
        title: 'Proveedor actualizado con exito',
        description: (firstName: string, lastName: string) => `${firstName} ${lastName} fue actualizado con éxito`,
      }
    }
  }
}

const StyledContainer = styled(Container)`
  padding-top: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
`

const FormContainer = styled(Col)`
  & > form {
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    border-radius: ${({ theme }) => theme.border.radius.base};
  }
`

export const CreateSupplierPage = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const [currentSuplier, setCurrentSupplier] = useState<SupplierDetail | null>(null)

  const [titleText, setTitleText] = useState(`${texts.create} ${texts.title}`)

  const navigate = useNavigate()
  
  const location = useLocation()

  const { open: errorHandle } = useErrorHandlerContext()

  const [getSupplier, {loading}] = useGetSupplierDetailLazyQuery()

  useEffect(() => {
    console.log(location.state)
    if (location.state) {
      if (location.state.readOnly) setTitleText(`${texts.title} ${texts.detail}`)
      else setTitleText(`${texts.edit} ${texts.title}`)
     
      getSupplier({
        variables: {
          id: location.state.supplierId,
        }
      })
      .then((response) => {
        setCurrentSupplier(response.data?.supplier || null)
      })
      .catch(errorHandle)
    }
  }, [location.state?.tenantId, getSupplier])

  const onCreateSuccess = (response: FetchResult<CreateSupplierMutation> | FetchResult<UpdateSupplierMutation>) => {
    if (response.data && 'createSupplier' in response.data) {
      const user = response.data?.createSupplier
      if (user.__typename === 'SupplierOutput') {
        confirmDialogModalOpen({
          title: texts.confirmation.create.title,
          description: texts.confirmation.create.description(user.firstName, user.lastName),
        })
        navigate(-1)
      }
    } else if (response.data && 'updateSupplier' in response.data) {
      const user = response.data?.updateSupplier
      confirmDialogModalOpen({
        title: texts.confirmation.update.title,
        description: texts.confirmation.update.description(user.firstName, user.lastName)
      })
      navigate(-1)
    } 
  }

  return (
    <StyledContainer>
      {
        loading
        ? (
          <Row>
            <Col>
              <LoadingSpinner />
            </Col>
          </Row>
        )
        : (
          <>
            <Row align='center'>
              <Col xs="content">
                <BackButton />
              </Col>
              <Col>
                <h2>{titleText}</h2>
              </Col>
            </Row>
            <Row>
              <FormContainer>
                <CreateSupplierForm
                  supplierDetail={currentSuplier}
                  activity={location.state ? 'updating' : 'creating'}
                  readOnly={!!location.state?.readOnly}
                  onSuccess={onCreateSuccess}
                />
              </FormContainer>
            </Row>
          </>
        )
      }
    </StyledContainer>
  )
}